import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../common/Modal';
import { checkShareStatus, shareTripToCommunity } from '../../services/plansApi';
import { ApiError } from '../../services/apiBase';
import { markPublishPending, onPublishStatus, watchPublish, type ShareStatus } from '../../utils/publishWatch';
import { outcomeLines, completedTripPrompt, FAILED_STATES } from '../../utils/publishOutcome';
import { useToast } from '../common/Toast';
import { MapPin, Calendar, Layers } from 'lucide-react';
import './ShareTripModal.css';

interface ShareTripModalProps {
  itineraryId: number;
  destination: string;
  durationDays: number | null;
  activityCount: number;
  endDate: string | null;
  sourcePlan: number | null | undefined;
  /** The itinerary's visibility — names the origin on the outcome line. */
  visibility?: string | null;
  /** Increment 5: the "Not now" key has YIELDED to a failed completed-trip
   *  share (the page's whole rule said `available`), so the modal renders
   *  despite it — opened from the strip's button, never on page load. */
  retry?: boolean;
  onClose: () => void;
  onShared?: () => void;
}

/** Best-effort: storage may be unavailable, and that is not a failed share. */
function dismissForever(itineraryId: number): void {
  try { localStorage.setItem(DISMISS_KEY(itineraryId), '1'); } catch { /* storage unavailable */ }
}

const DISMISS_KEY = (id: number) => `share_dismissed_${id}`;
const MIN_EDITS = 3;

export const ShareTripModal: React.FC<ShareTripModalProps> = ({
  itineraryId,
  destination,
  durationDays,
  activityCount,
  endDate,
  sourcePlan,
  visibility,
  retry = false,
  onClose,
  onShared,
}) => {
  const [loading, setLoading] = useState(false);
  const [editCount, setEditCount] = useState<number | null>(null);
  const [alreadyShared, setAlreadyShared] = useState(false);
  const [success, setSuccess] = useState(false);
  // The LAST status fetched (increment 5): the success view and the retry
  // view render the outcome line from it, and every poll updates it.
  const [status, setStatus] = useState<ShareStatus | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    let live = true;
    checkShareStatus(itineraryId)
      .then((res) => {
        if (!live) return;
        setEditCount(res.edit_count);
        setAlreadyShared(res.shared);
        setStatus(res);
      })
      .catch(() => {
        if (live) setEditCount(0);
      });
    const off = onPublishStatus(itineraryId, (s) => { if (live) setStatus(s); });
    return () => { live = false; off(); };
  }, [itineraryId]);

  // The WHOLE rule at the button, against the freshest status this modal has
  // — not just the edit count. The page decided to offer the prompt from a
  // read that can be minutes old; between then and the press another device
  // can make the plan public or the owner can take it down, and either makes
  // this POST the wrong door (external round 484). The server refuses both
  // too — that is the boundary that cannot go stale — and this keeps the
  // button honest before it is pressed.
  const canShare = editCount !== null && status !== null && completedTripPrompt({
    sourcePlan, endDate, dismissed: false, shared: alreadyShared, editCount,
    visibility, state: status.publish?.state ?? 'none',
  }) !== 'none';

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY(itineraryId), '1');
    onClose();
  }, [itineraryId, onClose]);

  const handleShare = useCallback(async () => {
    setLoading(true);
    // The SERVER decides whether this succeeded; only its failure is one.
    try {
      await shareTripToCommunity(itineraryId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to share trip';
      // Keyed on the server's stable code, not the message text.
      const code = err instanceof ApiError ? (err.body as { code?: string }).code : undefined;
      if (code === 'already_shared' || code === 'already_publishing' || msg.includes('already shared')) {
        showToast('You have already shared this trip.', 'error');
        dismissForever(itineraryId);
        onClose();
      } else if (code === 'use_share_sheet' || code === 'unpublished_by_owner') {
        // The server saw a state this modal could not: the plan went public
        // (its publish belongs to the sheet), or the owner took it down.
        // Either way this prompt is finished — say why and close it.
        showToast(msg, 'error');
        dismissForever(itineraryId);
        onClose();
      } else {
        showToast(msg, 'error');
      }
      setLoading(false);
      return;
    }
    // Everything after the ack is best-effort local state: the plan is
    // staged whatever this browser's storage does, and reporting a landed
    // share as failed would leave the observers un-armed.
    setSuccess(true);
    showToast('Trip submitted — we check it before it appears in Explore.', 'success');
    dismissForever(itineraryId);
    // The success view reads the STATUS, not a sentence written in advance.
    checkShareStatus(itineraryId).then(setStatus).catch(() => { /* the watch's polls will */ });
    markPublishPending(itineraryId);
    // Watch actively too: the modal closes at once, and the page's on-visit
    // check is for a LATER visit.
    void watchPublish(itineraryId);
    // `onShared` (and `onClose` on the duplicate branch) are the PARENT's, and
    // deliberately outside the request's failure branch: a throw from a parent
    // callback surfaces as the parent's bug, never as a failed share.
    onShared?.();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId, showToast, onClose]);

  // Don't show if not forked or trip not ended
  if (!sourcePlan || !endDate) return null;
  const tripEnd = new Date(endDate + 'T23:59:59');
  if (tripEnd >= new Date()) return null;
  // A successful share sets the "Not now" key itself (`dismissForever`), so
  // the success view must be decided BEFORE the key is read — the previous
  // order hid the view the share had just earned.
  const publish = status?.publish;
  const lines = publish ? outcomeLines(publish, { visibility }) : [];
  const failed = publish ? FAILED_STATES.includes(publish.state) : false;
  const problems = publish?.problems || [];

  if (success) {
    return (
      <Modal isOpen onClose={onClose} title="Shared!" size="sm">
        <div className="share-trip-modal__success">
          {lines.length === 0 ? (
            <p>Publishing — we check it first. Come back to this plan to see where it stands.</p>
          ) : lines.map((line) => (
            <p key={line.text} className={`share-trip-modal__line share-trip-modal__line--${line.tone}`} role="status">{line.text}</p>
          ))}
          <button className="share-trip-modal__btn share-trip-modal__btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  let dismissed = false;
  try { dismissed = Boolean(localStorage.getItem(DISMISS_KEY(itineraryId))); } catch { /* storage unavailable */ }
  if (dismissed && !retry) return null;

  return (
    <Modal isOpen onClose={handleDismiss} title={failed ? 'Share your trip again?' : 'Share your trip with the community?'} size="sm">
      <div className="share-trip-modal__body">
        {failed && (
          <div className="share-trip-modal__problems" role="status">
            {lines.map((line) => (
              <p key={line.text} className={`share-trip-modal__line share-trip-modal__line--${line.tone}`}>{line.text}</p>
            ))}
            {problems.length > 1 && (
              <ul>
                {problems.map((pr) => (
                  <li key={pr.check}><strong>{pr.label}</strong>{pr.detail ? `: ${pr.detail}` : ''}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <p className="share-trip-modal__desc">
          Your personal info (dates, names, contact details) will be automatically removed.
          The trip will enter a brief moderation review before appearing in the community.
        </p>

        <div className="share-trip-modal__preview">
          <div className="share-trip-modal__preview-title">What will be shared</div>
          <div className="share-trip-modal__preview-items">
            {destination && (
              <div className="share-trip-modal__preview-item">
                <MapPin size={14} />
                <span>{destination}</span>
              </div>
            )}
            {durationDays && (
              <div className="share-trip-modal__preview-item">
                <Calendar size={14} />
                <span>{durationDays} day{durationDays !== 1 ? 's' : ''}</span>
              </div>
            )}
            <div className="share-trip-modal__preview-item">
              <Layers size={14} />
              <span>{activityCount} activit{activityCount !== 1 ? 'ies' : 'y'}</span>
            </div>
          </div>
        </div>

        {editCount !== null && editCount < MIN_EDITS && (
          <p className="share-trip-modal__insufficient">
            Customize your trip more before sharing (need at least {MIN_EDITS} changes)
          </p>
        )}

        {alreadyShared && (
          <p className="share-trip-modal__already-shared">
            You have already shared this trip.
          </p>
        )}

        <div className="share-trip-modal__actions">
          <button
            className="share-trip-modal__btn share-trip-modal__btn--secondary"
            onClick={handleDismiss}
          >
            Not now
          </button>
          <button
            className="share-trip-modal__btn share-trip-modal__btn--primary"
            onClick={handleShare}
            disabled={!canShare || loading}
          >
            {loading ? 'Sharing...' : 'Share'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareTripModal;
